import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import i18next from 'i18next';
import moment from 'moment-timezone';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';

// Utils
import { convertSPPQuantityInMasterUnit } from '@orders/utils/computePackagingToSupplierProduct';
import { getSendTransferListModalConfig } from '@stocks/StocksInventories/Transfers/utils/transferModals';

// Hooks
import { useDatePickerState } from '@hooks/useDatePickerState';

// Services
import storeService from '@services/store';
import transfersService from '@services/transfers';

// Commons
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { TRANSFER_DETAILS_VIEW } from '@stocks/StocksInventories/Transfers/utils/constants';
import EmptyState from '@commons/EmptyState';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Components
import { formatSPAndRecipesPayloadForCreation } from '../../utils/formatPayload';
import { getClientInfo } from '@selectors/client';
import { PageContainer } from './styledComponents';
import TransferContent from '../TransferContent';
import TransfersHeader from '../Header';

export const TransfersCreation = (props) => {
  const {
    pageLoading,
    pageLoaded,
    showErrorMessage,
    showSuccessMessage,
    openGenericModal,
    refreshGenericModal,
    history,
    user,
    client: { storeName },
  } = props;

  const translatedStoreNamePlural = getClientStoreNameTranslation(storeName, true);

  const location = useLocation();

  // Extract and parse the isSender parameter from URL query string
  const queryParams = qs.parse(location.search);
  const isSender = queryParams.isSender === 'true';

  const [transferList, setTransferList] = useState({
    reference: 'TRA-',
    status: 0,
    deliveryTime: null,
    isSender,
  });

  const [allStoresOfClient, setAllStoresOfClient] = useState([]);
  const [emailsTo, setEmailsTo] = useState([]);
  const [emailsCC, setEmailsCC] = useState([]);
  const [recipesToDisplay, setRecipesToDisplay] = useState([]);
  const [supplierProductsToDisplay, setSupplierProductsToDisplay] = useState([]);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const datePickerState = useDatePickerState(null);

  /* EFFECT */

  useEffect(() => {
    getAllStoresOfClient();
  }, []);

  // Track previous values to detect changes
  const prevConfigRef = useRef({
    senderStoreId: transferList.senderStoreId,
    recipientStoreId: transferList.recipientStoreId,
    date: datePickerState.date,
  });

  useEffect(() => {
    const currConf = {
      senderStoreId: transferList.senderStoreId,
      recipientStoreId: transferList.recipientStoreId,
      date: datePickerState.date,
    };

    const prevValues = prevConfigRef.current;

    // Check if all required values are set
    const allValuesSet = currConf.senderStoreId && currConf.recipientStoreId && currConf.date;

    if (!allValuesSet) {
      // Update ref even if not all values are set
      prevConfigRef.current = currConf;
      return;
    }

    if (
      (allValuesSet &&
        (!prevValues.senderStoreId || !prevValues.recipientStoreId || !prevValues.date)) || // Case 1: All values are set and this is the first time (at least one was undefined before)
      prevValues.senderStoreId !== currConf.senderStoreId // Case 2: Only senderStoreId changed (and all values are set)
    ) {
      getAvailableSupplierProductsAndRecipes();
    }

    // Update ref with current values
    prevConfigRef.current = currConf;
  }, [transferList.senderStoreId, transferList.recipientStoreId, datePickerState.date]);

  useEffect(() => {
    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const config = getSendTransferListModalConfig({
      transferList,
      emailsTo,
      emailsCC,
      setEmailsTo,
      setEmailsCC,
      handleTransferListSend,
    });

    refreshGenericModal(config);
  }, [emailsTo, emailsCC]);

  /* FONCTIONS */

  const getAvailableSupplierProductsAndRecipes = async () => {
    setIsFormDirty(false);

    pageLoading();

    try {
      const { supplierProducts, recipes } =
        await transfersService.getSupplierProductsAndRecipesByStoreId(
          transferList.senderStoreId,
          moment().format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        );

      const formattedSupplierProducts = supplierProducts.map((supplierProduct) => {
        const packagingUsedInOrder = supplierProduct.packagings.find(
          (packaging) => packaging.isUsedInOrder,
        );

        return {
          ...supplierProduct,
          supplierName: get(supplierProduct, 'lnkSupplierSupplierproductrel.name', null),
          isIngredientStrategic: get(
            supplierProduct,
            'lnkEntitySupplierproductrel.isStrategic',
            false,
          ),
          selectedPackagingId:
            packagingUsedInOrder?.id || get(supplierProduct, 'packagings[0].id', null),
          supplierProductUnit: supplierProduct.packagings[0]?.unit || null,
          packagings: supplierProduct.packagings.map((packaging) => ({
            ...packaging,
            selectedQuantity: 0,
            quantityInMasterUnit: convertSPPQuantityInMasterUnit(
              packaging.id,
              supplierProduct.packagings,
            ),
          })),
        };
      });

      const formattedRecipes = recipes.map((recipe) => ({
        ...recipe,
        selectedQuantity: 0,
      }));

      setSupplierProductsToDisplay(formattedSupplierProducts);
      setRecipesToDisplay(formattedRecipes);
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DETAILS_FETCH_FAILURE'));
    } finally {
      pageLoaded();
    }
  };

  const getAllStoresOfClient = async () => {
    pageLoading();

    try {
      const fetchedClientStores = await storeService.getStoresOfClient();

      const formattedStores = fetchedClientStores.map((store) => ({
        ...store,
        transferEmails: store.transferEmails?.split(',').filter((e) => e?.trim()) ?? [],
      }));

      setAllStoresOfClient(formattedStores);
    } catch {
      showErrorMessage(
        i18next.t('GENERAL.STORES_CLIENT_FETCH_FAILURE', {
          storeName: translatedStoreNamePlural,
        }),
      );
    } finally {
      pageLoaded();
    }
  };

  const updateTransferList = (keyValuePairs) => {
    setTransferList({ ...transferList, ...keyValuePairs });
  };

  const updateSPsToDisplay = (supplierProducts) => {
    setSupplierProductsToDisplay(supplierProducts);
    setIsFormDirty(true);
  };

  const openSendModal = () => {
    if (!transferList.senderStoreId || !transferList.recipientStoreId) {
      return;
    }

    const { emailsTo, emailsCC } = allStoresOfClient.reduce(
      (acc, { id, transferEmails }) => {
        if (isEmpty(transferEmails)) {
          return acc;
        }

        // TO Emails
        if (isSender && id === transferList.recipientStoreId) {
          acc.emailsTo.push(...transferEmails);
        } else if (!isSender && id === transferList.senderStoreId) {
          acc.emailsTo.push(...transferEmails);
        }

        // CC Emails
        if (isSender && id === transferList.senderStoreId) {
          acc.emailsCC.push(...transferEmails);
        } else if (!isSender && id === transferList.recipientStoreId) {
          acc.emailsCC.push(...transferEmails);
        }

        return acc;
      },
      { emailsTo: [], emailsCC: [user.email] },
    );

    setEmailsTo(emailsTo);
    setEmailsCC(emailsCC);

    const config = getSendTransferListModalConfig({
      transferList,
      emailsTo,
      emailsCC,
      setEmailsTo,
      setEmailsCC,
      handleTransferListSend,
    });

    openGenericModal(config);
  };

  const handleTransferListSend = async (emailsTo, emailsCC, transferList) => {
    const transferListPayload = {
      ...transferList,
      sentAt: datePickerState.date.format(),
      isValidating: true,
      emailsTo: (emailsTo || []).map((email) => email.trim()).filter(Boolean),
      emailsCC: (emailsCC || []).map((email) => email.trim()).filter(Boolean),
    };
    // TODO: Update isSender in other statuses

    const { supplierProducts, recipes } = formatSPAndRecipesPayloadForCreation(
      supplierProductsToDisplay,
      recipesToDisplay,
    );

    try {
      pageLoading();

      await transfersService.createTransfer({
        transferList: transferListPayload,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.CREATION_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.CREATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const updateRecipesToDisplay = (recipes) => {
    setRecipesToDisplay(recipes);
    setIsFormDirty(true);
  };

  const displayEmptyState =
    !transferList.senderStoreId || !transferList.recipientStoreId || !datePickerState.date;

  return (
    <PageContainer>
      <TransfersHeader
        allStoresOfClient={allStoresOfClient}
        datePickerState={datePickerState}
        isFormDirty={isFormDirty}
        isSender={isSender}
        openSendModal={openSendModal}
        recipesToDisplay={recipesToDisplay}
        supplierProductsToDisplay={supplierProductsToDisplay}
        transferDetailsView={TRANSFER_DETAILS_VIEW.DRAFT}
        transferList={transferList}
        updateTransferList={updateTransferList}
      />
      {displayEmptyState ? (
        <EmptyState
          icon={'/images/inpulse/supplier.svg'}
          label={i18next.t('STOCKS.TRANSFERS.DETAILS_GLOBAL_EMPTY_STATE_TITLE')}
        />
      ) : (
        <TransferContent
          recipesToDisplay={recipesToDisplay}
          setRecipesToDisplay={updateRecipesToDisplay}
          setSupplierProductsToDisplay={updateSPsToDisplay}
          supplierProductsToDisplay={supplierProductsToDisplay}
          transferDetailsView={TRANSFER_DETAILS_VIEW.DRAFT}
          transferList={transferList}
        />
      )}
    </PageContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
  modal: state.modalReducer,
});

export default connect(mapStateToProps, mapDispatchToProps)(TransfersCreation);
